import type { SalesAction } from '@shared/types/sales-action';

/// 「更新」判定に必要な最小フィールド。
type UnseenUpdateFields = Pick<
  SalesAction,
  'created_by_account_id' | 'updated_by_account_id' | 'created_at' | 'updated_at'
>;

/// ある現在アクションが、 自分にとっての「更新」 (他ユーザーが追加・編集した、 まだ自分が
/// 見ていない変更) かどうかを判定する (Issue #524 PR3 / #578)。
///
/// `last_seen_at` と `created_by_account_id` / `updated_by_account_id` を使い、 自分の変更を
/// 除外する。 actor は CRM 担当者 (`app_accounts.id`) なので、 自分判定も current user の
/// account_id で行う (`sessionStore.accountId`)。 単なる `updated_at > last_seen_at` 比較には
/// しない (自分の編集や内部処理だけの `updated_at` 更新では出さない / 仕様 §4)。
///
/// - 他ユーザーが作成し、 自分の `last_seen_at` 以降なら「更新」。
/// - 他ユーザーが編集し、 自分の `last_seen_at` 以降なら「更新」。
/// - `last_seen_at` が null (= まだ一度もタブを開いていない) なら、 他ユーザーの作成・編集は
///   すべて未閲覧 = 「更新」として扱う。
/// - `currentUserAccountId` が不明 (未 provision 等) なときは自分の変更を除外できないので
///   false に倒す (誤って自分の変更を「更新」として出さないため)。
/// - 変更者が NULL の行 (作成者不明 / 投入データ) は他者起因と判定できないので出さない。
export function isUnseenUpdate(
  action: UnseenUpdateFields,
  lastSeenAt: string | null,
  currentUserAccountId: string | null,
): boolean {
  if (!currentUserAccountId) return false;

  const seen = lastSeenAt === null ? null : Date.parse(lastSeenAt);
  const after = (iso: string): boolean =>
    seen === null || Number.isNaN(seen) ? true : Date.parse(iso) > seen;

  // フィールド欠落 (rolling deploy / キャッシュされた旧フロント × 新バックエンドの API 名
  // ずれ #578) で `undefined !== viewer` を誤って他者更新と判定しないよう、 文字列であることを
  // 明示的に要求する (typeof チェック)。
  const createdByOther =
    typeof action.created_by_account_id === 'string' &&
    action.created_by_account_id !== currentUserAccountId;
  const updatedByOther =
    typeof action.updated_by_account_id === 'string' &&
    action.updated_by_account_id !== currentUserAccountId;

  return (
    (createdByOther && after(action.created_at)) ||
    (updatedByOther && after(action.updated_at))
  );
}

/// 表示対象の現在アクションのうち「更新」に該当する id 集合を作る。 行表示は
/// `unseenUpdateIds.has(action.id)` で「更新」バッジを出すかを決める。
export function buildUnseenUpdateIds(
  actions: ReadonlyArray<UnseenUpdateFields & { id: string }>,
  lastSeenAt: string | null,
  currentUserAccountId: string | null,
): Set<string> {
  const ids = new Set<string>();
  for (const action of actions) {
    if (isUnseenUpdate(action, lastSeenAt, currentUserAccountId)) ids.add(action.id);
  }
  return ids;
}
